import type React from 'react';
import { Box, Text } from '@nimbus-ds/components';
import { DataList } from '@nimbus-ds/patterns';
import { MoneyDto } from '@tiendanube/common';
import { Money } from 'commons/components';
import { SettledAmount } from '../../../../../../../components';

interface AmountRowProps {
  readonly id: string;
  readonly title: string;
  readonly amount: MoneyDto;
  readonly isStrong?: boolean;
  readonly isNegative?: boolean;
  readonly hideIfZero?: boolean;
  readonly isWarning?: boolean;
}

function AmountRow({
  id,
  title,
  amount,
  isStrong = false,
  isNegative = false,
  hideIfZero = false,
  isWarning = false,
}: Readonly<AmountRowProps>): React.JSX.Element | null {
  const { value, currency, settledAmount, isError } = amount;

  const numericValue = Number(value);

  if (hideIfZero && numericValue === 0) {
    return null;
  }

  const computedPrice =
    isNegative && numericValue !== 0 ? -Math.abs(numericValue) : numericValue;

  const getBackgroundColor = () => {
    if (isWarning) return 'warning-surface';
    if (isStrong) return 'neutral-surface';
    return 'transparent';
  };

  const getMoneyColor = () => {
    if (isWarning) return 'warning-textLow';
    if (isNegative) return 'danger-textLow';
    return 'neutral-textHigh';
  };

  return (
    <DataList.Row
      id={id}
      topDivider={false}
      padding="none"
      backgroundColor={getBackgroundColor()}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        paddingY="2"
        paddingX="4"
      >
        <Box flex="7 1 0">
          <Text
            color={isWarning ? 'warning-textLow' : 'neutral-textHigh'}
            fontWeight={isStrong ? 'bold' : undefined}
          >
            {title}
          </Text>
        </Box>
        <Box flex="3 1 0">
          {isError ? (
            <Text
              color="neutral-textHigh"
              fontWeight={isStrong ? 'bold' : undefined}
            >
              -
            </Text>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Money
                price={computedPrice}
                currency={currency}
                textAlign="right"
                fontWeight={isStrong ? 'bold' : undefined}
                color={getMoneyColor()}
              />
              {settledAmount && <SettledAmount amount={settledAmount} />}
            </Box>
          )}
        </Box>
      </Box>
    </DataList.Row>
  );
}

export default AmountRow;
